/**
 * The operator's in-progress edits to a photo-intake draft, made durable.
 *
 * Review edits used to live only in browser memory until commit, which was
 * survivable while extraction ran exactly once per draft. Re-opening a draft for
 * more photos breaks that: a second model pass would land on top of whatever the
 * operator had already typed and silently discard it.
 *
 * The fix is to record which fields the operator has taken ownership of. This
 * blob is stored SPARSELY — a key is present only if they actually changed that
 * field. Presence is the whole mechanism:
 *
 *   key absent   -> the model owns it; a re-extraction may freely replace it
 *   key present  -> the operator owns it; a re-extraction must leave it alone
 *
 * An explicit `null` is therefore meaningfully different from an absent key.
 * Clearing a field the model filled in is a deliberate act — "this label does
 * not say" — and must survive re-extraction exactly like a typed value does.
 *
 * Note what is deliberately NOT here: `food_grade`, `allergens` and `nutrition`
 * are read-only derivations of the extraction in the review pane, with no
 * control to edit them. Recording them as operator-owned would freeze them at
 * their first-pass values, which is precisely backwards — adding a nutrition
 * panel and re-extracting is the main reason to re-open a draft at all.
 */
import { z } from "zod";
import { calendarDate } from "@brewbase/db/calendar-date";
import { FoodClaimStatusEnum } from "@brewbase/db/types";
/** How the commit should treat the shared product row. Mirrors the review pane. */
export const PRODUCT_WRITE_MODES = ["keep", "fill"];
/**
 * Sparse — every field optional, and `.nullable()` where the value itself can be
 * null, so an absent key and a null value stay distinguishable after a JSON
 * round-trip. `.strict()` because a stray key here would be silently treated as
 * an operator-owned field forever.
 */
export const intakeReviewStateSchema = z
    .object({
    product_name: z.string(),
    manufacturer_name: z.string(),
    manufacturer_product_code: z.string(),
    ean_code: z.string(),
    ingredients_text: z.string(),
    nutrition_basis: z.string(),
    supplier_name: z.string(),
    lot_number: z.string(),
    expiry_date: calendarDate.nullable(),
    quantity_value: z.number(),
    quantity_unit: z.string(),
    manufacturer_id: z.number().int().nullable(),
    supplier_id: z.number().int().nullable(),
    product_id: z.number().int().nullable(),
    product_write: z.enum(PRODUCT_WRITE_MODES),
    non_gmo_status: z.nativeEnum(FoodClaimStatusEnum),
    vegan_status: z.nativeEnum(FoodClaimStatusEnum),
    vegetarian_status: z.nativeEnum(FoodClaimStatusEnum),
})
    .partial()
    .strict();
export const INTAKE_REVIEW_FIELDS = Object.keys(intakeReviewStateSchema.shape);
/** Whether the operator has taken ownership of a field. */
export function ownsField(state, field) {
    return state != null && Object.hasOwn(state, field);
}
/**
 * Lays the operator's edits over the model-derived values.
 *
 * `hasOwn` rather than a `?? base` fallback, so an explicitly cleared field
 * stays cleared instead of snapping back to whatever the label said.
 */
export function overlayReviewState(base, state) {
    if (!state)
        return base;
    const merged = { ...base };
    for (const field of INTAKE_REVIEW_FIELDS) {
        if (Object.hasOwn(state, field)) {
            // Validated on the way in, and the key sets are defined together above.
            merged[field] = state[field];
        }
    }
    return merged;
}
/**
 * Fields a fresh extraction actually moved, ignoring any the operator owns.
 *
 * Drives the "what changed" notice after re-extraction. Without it a re-read
 * silently rewrites parts of the form the operator had already looked at, which
 * is the failure this whole mechanism exists to avoid — the values are safe, but
 * a form that changes under you with no explanation is its own problem.
 */
export function reextractionChanges(before, after, state) {
    return INTAKE_REVIEW_FIELDS.filter((field) => !ownsField(state, field) && before[field] !== after[field]);
}
/**
 * The operator-owned fields implied by the form's current values.
 *
 * Ownership is derived by comparing against the model's reading rather than
 * recorded per control. One comparison covers every field, including the ones
 * reached through a combobox or a toggle, and it cannot drift out of sync with
 * the form the way a hand-maintained "touched" flag on each input would.
 *
 * The consequence is that retyping a value identical to the label's is not
 * ownership — which is the same rule `withReviewEdit` applies, and the right
 * one: confirming a field should not stop a later, better photo improving it.
 */
export function diffReviewState(model, current) {
    const state = {};
    for (const field of INTAKE_REVIEW_FIELDS) {
        if (current[field] !== model[field]) {
            state[field] = current[field];
        }
    }
    return state;
}
/**
 * Records an edit, or drops it back to model ownership.
 *
 * Passing a value equal to the model's own reading REMOVES the key rather than
 * storing it. Retyping what the label already said is not a correction, and
 * pinning it would stop a better later photo from improving the field.
 */
export function withReviewEdit(state, field, value, modelValue) {
    const next = { ...(state ?? {}) };
    if (value === modelValue)
        delete next[field];
    else
        next[field] = value;
    return next;
}
//# sourceMappingURL=intake-review-state.js.map